import { IssuersService } from '../../services/issuers.service';
import { IssuerSalesContactDetails, IssuerUserRoles } from '../../model/issuer-sales-contact';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { TransferService } from '../../services/transfer.service';
import { PlatformLocation } from '@angular/common';

interface DropdownItem {
  id: string;
  displayValue: string;
}

interface IssuerContactWithFilter extends IssuerSalesContactDetails {
  searchTextValue?: string;
}

@Component({
    selector: 'deltacapita-edit-issuer-contact',
    templateUrl: './edit-issuer-contact.component.html',
    styleUrls: ['./edit-issuer-contact.component.scss'],
    standalone: false
})
export class EditIssuerContactComponent implements OnInit {
  @Input() modal: NgbModalRef;
  @ViewChild('loadingSpinner', { static: true }) loadingSpinner: TemplateRef<any>;

  loading = true;
  rolesDropdownData: DropdownItem[] = [
    { id: IssuerUserRoles.SALES, displayValue: 'Sales' },
    { id: IssuerUserRoles.MANAGER, displayValue: 'Manager' },
    { id: IssuerUserRoles.COMPLIANCE, displayValue: 'Compliance' },
    { id: IssuerUserRoles.LEGAL_APPROVER, displayValue: 'Legal Approver' },
  ];
  editIssuerContactForm: UntypedFormGroup;
  contactToBeEdited: IssuerContactWithFilter;
  allRegions: string[] = [];
  currentTaggedRegions: string[] = [];
  regionsToBeRemoved: string[] = [];
  isAllRegionChecked: boolean = false;
  isRegionDisabled: boolean = false;
  selectedRegions: string[];
  roleToBeEdited: string;
  allDesks: string[] = [];
  currentTaggedDesks: string[] = [];
  desksToBeRemoved: string[] = [];
  isAllDeskChecked: boolean = false;
  isDeskDisabled: boolean = false;
  selectedDesks: string[];

  constructor(
    private fb: UntypedFormBuilder,
    private issuersService: IssuersService,
    private dcAlertService: DcAlertService,
    private transferService: TransferService,
    location: PlatformLocation
  ) {
    location.onPopState((event) => {
      if (this.modal !== undefined) {
        this.modal.close();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.contactToBeEdited = this.transferService.getData();
    this.transferService.clearData();
    this.currentTaggedRegions = this.contactToBeEdited.salesregion;
    this.currentTaggedDesks = this.contactToBeEdited.salesdesk;
    this.roleToBeEdited = this.contactToBeEdited.role;
    this.issuersService.getSalesRegionsByIssuer(this.contactToBeEdited.issuerid).subscribe((data: any) => {
      this.allRegions = data.salesregion;
      // console.log(this.allRegions);
    });
    this.issuersService.getSalesDesksByIssuer(this.contactToBeEdited.issuerid).subscribe((data: any) => {
      this.allDesks = data.salesdesks;
      // console.log(this.allDesks);
    });

    if (this.roleToBeEdited === 'Compliance') {
      // amend to fully editable in compliance now
      this.isAllRegionChecked = false;
      this.isAllDeskChecked = false;
      this.isRegionDisabled = false;
      this.isDeskDisabled = false;
    } else if (this.roleToBeEdited === 'Manager' || this.roleToBeEdited === 'Legal Approver') {
      this.isAllDeskChecked = true;
      this.isDeskDisabled = true;
    }

    this.editIssuerContactForm = this.fb.group({
      name: new UntypedFormControl(null, Validators.required),
      email: new UntypedFormControl(null, Validators.required),
      role: new UntypedFormControl(null, Validators.required),
      salesdesk: this.fb.array([]),
      salesregion: this.fb.array([]),
      optoutdocalert: new UntypedFormControl(false),
    });

    this.editIssuerContactForm.patchValue(this.contactToBeEdited);

    this.initializeFormArray(this.currentTaggedRegions, 'salesregion');
    this.initializeFormArray(this.currentTaggedDesks, 'salesdesk');

    // set role behaviour for salesregions and salesdesks
    this.editIssuerContactForm.valueChanges.subscribe((values) => {
      // set how roles can dictate desks behaviour
      if (
        // values.role === 'Compliance' ||
        values.role === 'Legal Approver' ||
        values.role === 'Manager'
      ) {
        // reset to all regions
        this.isAllDeskChecked = true;
        this.isDeskDisabled = true;
        this.currentTaggedDesks = this.allDesks;
        this.initializeFormArray(this.allDesks, 'salesdesk');
        // double secure by adds all removed ones
        this.desksToBeRemoved.forEach((x) => {
          if (!this.salesdesk.controls.map((y) => y.value).includes(x)) {
            this.currentTaggedDesks.push(x);
            this.salesdesk.push(new UntypedFormControl(x));
          }
        });
        this.desksToBeRemoved = [];
        // set how roles can dictate regions behaviour, comment out now to allow compliance region editing
        // if (values.role === 'Compliance') {
        //   // reset to all regions
        //   this.isAllRegionChecked = true;
        //   this.isRegionDisabled = true;
        //   this.currentTaggedRegions = this.allRegions;
        //   this.initializeFormArray(this.allRegions, 'salesregion');
        //   // double secure by adds all removed ones
        //   this.regionsToBeRemoved.forEach((x) => {
        //     if (!this.salesregion.controls.map((y) => y.value).includes(x)) {
        //       this.currentTaggedRegions.push(x);
        //       this.salesregion.push(new FormControl(x));
        //     }
        //   });
        //   this.regionsToBeRemoved = [];
        // } else {
        this.isAllRegionChecked = false;
        this.isRegionDisabled = false;
        // }
      } else {
        this.isAllRegionChecked = false;
        this.isAllDeskChecked = false;
        this.isRegionDisabled = false;
        this.isDeskDisabled = false;
        // re-initialise with excluding currently un-ticked regions
        this.currentTaggedRegions = this.currentTaggedRegions.filter((x) => {
          return !this.regionsToBeRemoved.includes(x);
        });
        this.initializeFormArray(this.currentTaggedRegions, 'salesregion');
        this.isAllDeskChecked = false;
        // re-initialise with excluding currently un-ticked desks
        this.currentTaggedDesks = this.currentTaggedDesks.filter((x) => {
          return !this.desksToBeRemoved.includes(x);
        });
        this.initializeFormArray(this.currentTaggedDesks, 'salesdesk');
      }
    });

    this.loading = false;
  }

  private salesregion: UntypedFormArray;
  private salesdesk: UntypedFormArray;

  initializeFormArray(array: any[], groupControlName: string): void {
    if (groupControlName === 'salesregion') {
      this.salesregion = this.editIssuerContactForm.get('salesregion') as UntypedFormArray;
      let salesRegionValues = this.salesregion.controls.map((x) => x.value);
      array.forEach((x) => {
        if (!salesRegionValues.includes(x)) {
          this.salesregion.push(new UntypedFormControl(x));
        }
      });
    } else if (groupControlName === 'salesdesk') {
      this.salesdesk = this.editIssuerContactForm.get('salesdesk') as UntypedFormArray;
      let salesDesksValues = this.salesdesk.controls.map((x) => x.value);
      array.forEach((x) => {
        if (!salesDesksValues.includes(x)) {
          this.salesdesk.push(new UntypedFormControl(x));
        }
      });
    }
  }

  onDeskCheckboxChange(e) {
    if (e.target.checked && !this.currentTaggedDesks.includes(e.target.value)) {
      // store all ticked desks into form control
      this.salesdesk.push(new UntypedFormControl(e.target.value));
      this.currentTaggedDesks.push(e.target.value);
      if (this.desksToBeRemoved.includes(e.target.value)) {
        this.desksToBeRemoved = this.desksToBeRemoved.filter((x) => x !== e.target.value);
      }
    } else if (!e.target.checked) {
      // store all desks that have been un-ticked
      !this.desksToBeRemoved.includes(e.target.value) && this.desksToBeRemoved.push(e.target.value);
    }
  }

  onRegionCheckboxChange(e) {
    if (e.target.checked && !this.currentTaggedRegions.includes(e.target.value)) {
      // store all ticked regions into form control
      this.salesregion.push(new UntypedFormControl(e.target.value));
      this.currentTaggedRegions.push(e.target.value);
      if (this.regionsToBeRemoved.includes(e.target.value)) {
        this.regionsToBeRemoved = this.regionsToBeRemoved.filter((x) => x !== e.target.value);
      }
    } else if (!e.target.checked) {
      // store all regions that have been un-ticked
      !this.regionsToBeRemoved.includes(e.target.value) && this.regionsToBeRemoved.push(e.target.value);
    }
  }

  validateForm() {
    return this.editIssuerContactForm.valid && this.getCheckedDesks() && this.getCheckedRegions();
  }

  getCheckedDesks(): boolean {
    this.currentTaggedDesks = [...new Set(this.currentTaggedDesks)];
    this.currentTaggedDesks = this.currentTaggedDesks.filter((x) => !this.desksToBeRemoved.includes(x));
    const checkedCount: number = this.currentTaggedDesks.length;
    return checkedCount > 0;
  }

  getCheckedRegions(): boolean {
    this.currentTaggedRegions = [...new Set(this.currentTaggedRegions)];
    this.currentTaggedRegions = this.currentTaggedRegions.filter((x) => !this.regionsToBeRemoved.includes(x));
    const checkedCount: number = this.currentTaggedRegions.length;
    return checkedCount > 0;
  }

  async updateIssuerContact(): Promise<void> {
    const updatedIssuerContact = this.editIssuerContactForm.getRawValue();

    // filter out newly un-ticked regions
    this.selectedRegions = updatedIssuerContact.salesregion.filter((x) => !this.regionsToBeRemoved.includes(x));
    updatedIssuerContact.salesregion = [...new Set(this.selectedRegions)];

    // filter out newly un-ticked desks
    this.selectedDesks = updatedIssuerContact.salesdesk.filter((x) => !this.desksToBeRemoved.includes(x));
    updatedIssuerContact.salesdesk = [...new Set(this.selectedDesks)];

    this.issuersService.updateIssuerSalesContact(this.contactToBeEdited.id, updatedIssuerContact).subscribe(
      (contact: IssuerSalesContactDetails) => {
        this.dcAlertService.showAlert({
          type: 'success',
          message: `Successfully updated contact: ${contact.name}(${contact.email})`,
        });
        this.issuersService.refreshContactsSubject.next();

        this.modal.close();
      },
      (e) => {
        this.loading = false;
        console.log(e);
        this.dcAlertService.showAlert({
          type: 'danger',
          message: 'Error updating issuer sales contact!',
        });
      },
      () => {
        //reload after status update with filter still in place
        this.transferService.isClicked = true;
        if (this.contactToBeEdited.searchTextValue) {
          this.transferService.setData(this.contactToBeEdited.email);
        }
        this.transferService.notifyOther({ toRefresh: true, keysToRemove: ['issuerContacts'] });
      }
    );
  }
}
